import { useState, useEffect } from 'react';
import axios from 'axios';
import { useCompany } from '../context/CompanyContext';
import { Plus, Search, Loader2, UserCheck, MoreHorizontal, Pencil, Trash2, Mail, Phone, Briefcase } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { ExportImportButtons } from '../components/ExportImportButtons';
import { useExportImport } from '../hooks/useExportImport';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const EMPTY = { name: '', role: '', email: '', phone: '', department: '', start_date: '', notes: '' };

export default function EmployeesPage() {
  const { currentCompany } = useCompany();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);

  const { handleExportCSV, handleImportCSV, exporting, importing } = useExportImport({
    exportUrl: '/api/employees/export/csv',
    importUrl: '/api/employees/import/csv',
    companyId: currentCompany?.id,
    onImportSuccess: () => fetchEmployees(),
    entityName: 'employees'
  });

  useEffect(() => {
    if (currentCompany) fetchEmployees();
  }, [currentCompany, search]);

  const fetchEmployees = async () => {
    setLoading(true);
    try {
      const params = { company_id: currentCompany.id };
      if (search) params.search = search;
      const res = await axios.get(`${API_URL}/api/employees`, { params, withCredentials: true });
      setEmployees(res.data);
    } catch { toast.error('Failed to load employees'); }
    finally { setLoading(false); }
  };

  const handleAdd = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/employees`, { company_id: currentCompany.id, ...form }, { withCredentials: true });
      toast.success('Employee added');
      setIsAddOpen(false);
      setForm(EMPTY);
      fetchEmployees();
    } catch { toast.error('Failed to add employee'); }
    finally { setSubmitting(false); }
  };

  const handleEdit = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSubmitting(true);
    try {
      await axios.put(`${API_URL}/api/employees/${selected.id}`, form, { withCredentials: true });
      toast.success('Employee updated');
      setIsEditOpen(false);
      setSelected(null);
      setForm(EMPTY);
      fetchEmployees();
    } catch { toast.error('Failed to update'); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API_URL}/api/employees/${deleteTarget.id}`, { withCredentials: true });
      toast.success('Employee deleted');
      setDeleteTarget(null);
      fetchEmployees();
    } catch { toast.error('Failed to delete'); }
  };

  const openEdit = (emp) => {
    setSelected(emp);
    setForm({ name: emp.name, role: emp.role || '', email: emp.email || '', phone: emp.phone || '', department: emp.department || '', start_date: emp.start_date || '', notes: emp.notes || '' });
    setIsEditOpen(true);
  };

  if (!currentCompany) return <div className="flex items-center justify-center h-64"><p className="text-gray-500">Please select a company</p></div>;

  const employeeForm = (
    <div className="space-y-4 py-2">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2"><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" data-testid="employee-name-input" /></div>
        <div className="space-y-2"><Label>Role</Label><Input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} placeholder="e.g. Manager" data-testid="employee-role-input" /></div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="email@example.com" /></div>
        <div className="space-y-2"><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone number" /></div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2"><Label>Department</Label><Input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} placeholder="e.g. Operations" /></div>
        <div className="space-y-2"><Label>Start Date</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
      </div>
      <div className="space-y-2"><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Additional notes..." /></div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="employees-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Employees</h1>
          <p className="text-gray-500 mt-1">Manage your team members</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <ExportImportButtons onExportCSV={handleExportCSV} onImportCSV={handleImportCSV} exporting={exporting} importing={importing} testIdPrefix="employees" />
          <Button onClick={() => { setForm(EMPTY); setIsAddOpen(true); }} data-testid="add-employee-btn"><Plus className="w-4 h-4 mr-2" />Add Employee</Button>
        </div>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input placeholder="Search employees..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" data-testid="search-employees" />
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
      ) : employees.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <UserCheck className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">{search ? 'No employees match' : 'No employees yet'}</h3>
          <p className="text-gray-500 mb-4">Add your first team member</p>
          {!search && <Button onClick={() => { setForm(EMPTY); setIsAddOpen(true); }}><Plus className="w-4 h-4 mr-2" />Add Employee</Button>}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Department</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Start Date</th>
                <th className="px-4 py-3 w-12"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {employees.map((emp) => (
                <tr key={emp.id} className="hover:bg-gray-50" data-testid={`employee-row-${emp.id}`}>
                  <td className="px-4 py-3 font-medium text-gray-900">{emp.name}</td>
                  <td className="px-4 py-3 text-gray-600">{emp.role || '-'}</td>
                  <td className="px-4 py-3"><span className={`${emp.department ? 'px-2 py-0.5 bg-gray-100 rounded text-xs font-medium text-gray-700' : 'text-gray-400 text-sm'}`}>{emp.department || '-'}</span></td>
                  <td className="px-4 py-3">{emp.email ? <a href={`mailto:${emp.email}`} className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"><Mail className="w-3.5 h-3.5" />{emp.email}</a> : <span className="text-gray-400 text-sm">-</span>}</td>
                  <td className="px-4 py-3">{emp.phone ? <span className="text-sm text-gray-600 flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{emp.phone}</span> : <span className="text-gray-400 text-sm">-</span>}</td>
                  <td className="px-4 py-3 text-gray-500 text-sm">{emp.start_date || '-'}</td>
                  <td className="px-4 py-3">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8"><MoreHorizontal className="w-4 h-4" /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEdit(emp)}><Pencil className="w-4 h-4 mr-2" />Edit</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setDeleteTarget(emp)} className="text-red-600"><Trash2 className="w-4 h-4 mr-2" />Delete</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
        <DialogContent data-testid="add-employee-dialog"><DialogHeader><DialogTitle>Add Employee</DialogTitle><DialogDescription>Add a new team member.</DialogDescription></DialogHeader>{employeeForm}<DialogFooter><Button variant="outline" onClick={() => setIsAddOpen(false)}>Cancel</Button><Button onClick={handleAdd} disabled={submitting} data-testid="save-employee-btn">{submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add'}</Button></DialogFooter></DialogContent>
      </Dialog>
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent><DialogHeader><DialogTitle>Edit Employee</DialogTitle></DialogHeader>{employeeForm}<DialogFooter><Button variant="outline" onClick={() => setIsEditOpen(false)}>Cancel</Button><Button onClick={handleEdit} disabled={submitting}>{submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}</Button></DialogFooter></DialogContent>
      </Dialog>
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Delete Employee</AlertDialogTitle><AlertDialogDescription>Delete "{deleteTarget?.name}"?</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
